// Mirrors apps/shared/pkg/feedcontract's visible_item_limit contract: an
// optional per-surface cap on how many ranked content cards a supported
// carousel renders to customers. Display types not listed here render
// every fetched item, exactly as before this setting existed.
export const VISIBLE_ITEM_LIMIT_MINIMUM = 1
export const VISIBLE_ITEM_LIMIT_MAXIMUM = 50

// Display types whose renderer presents a bounded, ranked list of content
// cards and can therefore enforce visible_item_limit. Other display types
// either render a single card (floating/horizontal player), an unbounded
// scroll/grid (story_block, grid, hero, player_deck), or a non-card list
// (circle/vertical stories) where a card cutoff does not apply.
const VISIBLE_ITEM_LIMIT_DISPLAY_TYPES: Readonly<Record<string, true>> = Object.freeze({
  carousel: true,
  horizontal_carousel: true,
  dynamic_carousel: true,
})

export function displayTypeSupportsVisibleItemLimit(
  displayType: string | null | undefined,
): boolean {
  return Boolean(displayType && VISIBLE_ITEM_LIMIT_DISPLAY_TYPES[displayType])
}

/**
 * Resolves the configured visible_item_limit for a supported carousel
 * display type. Returns null when the field is absent, out of range, or
 * the display type does not enforce a card cutoff — callers must render
 * every item in that case, preserving current unlimited behavior.
 */
export function resolveVisibleItemLimit(
  config: Record<string, unknown> | null | undefined,
  displayType: string | null | undefined,
): number | null {
  if (!displayTypeSupportsVisibleItemLimit(displayType)) return null

  const value = config?.visible_item_limit
  if (
    typeof value !== 'number'
    || !Number.isInteger(value)
    || value < VISIBLE_ITEM_LIMIT_MINIMUM
    || value > VISIBLE_ITEM_LIMIT_MAXIMUM
  ) {
    return null
  }
  return value
}

/**
 * Applies a resolved limit to a ranked item list. Returns the original
 * array reference when no limit applies or nothing is trimmed, so callers
 * can rely on referential equality to skip redundant work.
 */
export function applyVisibleItemLimit<T>(
  items: readonly T[],
  limit: number | null,
): readonly T[] {
  if (limit === null || items.length <= limit) return items
  return items.slice(0, limit)
}
